<template>
    <v-app style="background-color: transparent">
        <ConfirmDialog>
            <BackgroundArea>
                <Suspense>
                    <template v-if="routeResolved">
                        <template v-if="route.meta.layout === 'bare'">
                            <bare :key="route.path" />
                        </template>
                        <template v-else-if="route.meta.layout === 'epub-reader'">
                            <epub-reader :key="route.path" />
                        </template>
                        <template v-else>
                            <default :key="route.path" />
                        </template>
                    </template>
                </Suspense>
                <span id="is_vue" style="display: none"></span>
                <v-snackbar
                    v-model="snack.show"
                    :color="snack.color"
                    :timeout="snack.color === 'error' ? -1 : 5000"
                    close-on-back
                    close-on-content-click
                    vertical
                    style="z-index: 20000"
                    max-width="800px"
                >
                    <div>{{ snack.message }}</div>
                    <div v-if="snack.html" class="mt-4">
                        <label>Details:</label>
                        <div class="rounded pa-1 snack-details" v-html="sanitizedSnackHtml"></div>
                    </div>
                    <template v-if="snack.color === 'error'" #actions>
                        <v-btn variant="text" @click="snack.show = false"> Close </v-btn>
                    </template>
                </v-snackbar>
            </BackgroundArea>
        </ConfirmDialog>
    </v-app>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { FetchError } from 'ofetch';
import sanitizeHtml from 'sanitize-html';
import useHostname from '@/composables/useHostname';

const { snack, snackbarError } = useSnackbar();

const route = useRoute();
const router = useRouter();
const auth = useAuth();
const { isLoggedIn, userType } = storeToRefs(auth);
const routeResolved = ref(false);

function redirectPathIfRoot(): string | undefined {
    if (route.path !== '/') {
        return undefined;
    }

    const host = useHostname();

    let path = '/landing';
    if (host.vhost === 'staff') {
        path = '/staff/login';
    }

    if (isLoggedIn.value) {
        if (userType.value === 'pupil') {
            path = '/pupil/change-book';
        } else if (userType.value === 'teacher') {
            path = '/teacher/home';
        } else if (userType.value === 'admin') {
            path = '/admin/home';
        } else if (userType.value === 'staff') {
            path = '/staff/home';
        }
    }
    console.log(`Redirecting to ${path} based on userType: ${userType.value}`);
    return path;
}

watch(route, () => {
    const path = redirectPathIfRoot();
    if (path) {
        router.push(path);
    } else {
        routeResolved.value = true;
    }
});

watch([isLoggedIn, routeResolved], () => {
    if (!routeResolved.value || route.meta.noAuth) {
        return;
    }
    if (!isLoggedIn.value) {
        // Reload to clear any old state
        console.log('Not logged in - Redirecting to landing page', JSON.stringify(route), import.meta.env.BASE_URL);
        if (window.location.host.includes('staff')) {
            window.location.href = import.meta.env.BASE_URL + 'staff/login';
        } else {
            if (route.path !== '/landing') {
                window.location.href = import.meta.env.BASE_URL + 'landing';
            }
        }
    }
});

const sanitizedSnackHtml = computed(() => sanitizeHtml(snack.value.html || ''));

onErrorCaptured((error: any) => {
    if (error.response && error.response.status && error.response.status === 401) {
        window.location.href = import.meta.env.BASE_URL + 'logout';
    } else {
        if (error.data && error.data.message) {
            snackbarError(error.data.message);
        } else {
            snackbarError(error.message);
        }
        console.error(error);
    }
    return false;
});

window.addEventListener('unhandledrejection', (event) => {
    if (event.reason.name === 'FetchError') {
        const fe = event.reason as FetchError;

        if (fe.response && fe.response.status && fe.response.status === 401) {
            window.location.href = import.meta.env.BASE_URL + 'logout';
            return;
        }

        const message = `Unhandled fetch error: ${fe}`;

        const htmls: string[] = [];
        if (typeof fe.data === 'object') {
            if (fe.data.stack) {
                htmls.push(fe.data.stack);
                delete fe.data.stack;
            }
            htmls.push(`<code>${JSON.stringify(fe.data)}</code>`);
        }
        if (typeof fe.data === 'string') {
            htmls.push(`<code>${fe.data}</code>`);
        }
        if (fe.response?.headers) {
            const hs: string[] = [];
            fe.response?.headers.forEach((v, k) => {
                hs.push(`${k}: ${v}`);
            });
            htmls.push(`<br/><code>${JSON.stringify(hs)}</code>`);
        }

        snackbarError(message, htmls.join('<br/>'));
        console.error(message, htmls.join('<br/>'));
    } else {
        console.error(event);
        if (event.reason.sourceURL?.includes('stonly')) {
            // Stonly errors are handled by the Stonly component
            return;
        }
        const message = `Unhandled error: ${event.reason}`;
        snackbarError(message);
    }
});
</script>
<style lang="scss">
.snack-details {
    border: 1px solid #aaaaaa;
    font-size: 0.8em;
    color: #aaaaaa;
}
</style>
